import { useMemo } from "react"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts"

// Stacked vertical bar chart. Used by the SLA Performance "Breaches by
// type" panel — three stacks per day for first-response / idle /
// resolution. Generic enough that other analytics pages can reuse.

export type StackedBarSeries = {
  id: string
  label: string
  data: number[]
  color: string
}

type Props = {
  dates: string[]
  series: StackedBarSeries[]
  /** Fixed pixel height. Ignored when `fill` is true. */
  height?: number
  /** When true, the chart fills the parent's height (parent must have
   *  a determined height — e.g. `flex-1` inside a flex column). */
  fill?: boolean
  formatY?: (n: number) => string
  formatTooltip?: (n: number) => string
  emptyMessage?: string
  showLegend?: boolean
}

export function StackedBarChart({
  dates,
  series,
  height = 260,
  fill = false,
  formatY,
  formatTooltip,
  emptyMessage = "No data in this window yet.",
  showLegend = false,
}: Props) {
  const data = useMemo(() => {
    return dates.map((d, i) => {
      const row: Record<string, string | number> = { date: d }
      for (const s of series) row[s.id] = s.data[i] ?? 0
      return row
    })
  }, [dates, series])

  const hasAny = data.some((row) => series.some((s) => Number(row[s.id]) > 0))
  if (!hasAny) {
    if (fill) {
      return (
        <div className="grid h-full w-full place-items-center text-sm text-app-muted">
          {emptyMessage}
        </div>
      )
    }
    return (
      <div className="grid place-items-center text-sm text-app-muted" style={{ height }}>
        {emptyMessage}
      </div>
    )
  }

  const containerHeight: number | string = fill ? "100%" : height

  return (
    // minHeight gives Recharts a non-zero fallback dimension when the
    // parent's flex-determined height isn't computed at first render —
    // otherwise it warns "width(-1) and height(-1) of chart should be
    // greater than 0". Width auto-fills via the parent's w-full.
    <ResponsiveContainer width="100%" height={containerHeight as number} minHeight={fill ? 160 : undefined}>
      <BarChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
        <XAxis dataKey="date" stroke="#888" tick={{ fontSize: 11 }} tickFormatter={fmtShortDate} />
        <YAxis stroke="#888" tick={{ fontSize: 11 }} tickFormatter={(v: number) => (formatY ? formatY(v) : String(v))} />
        <Tooltip
          contentStyle={{ background: "#1f1f23", border: "1px solid #333", borderRadius: 8, fontSize: 12 }}
          labelStyle={{ color: "#aaa" }}
          itemStyle={{ color: "#fff" }}
          formatter={(v) => {
            if (v == null) return "—"
            const n = typeof v === "number" ? v : Number(v)
            return formatTooltip ? formatTooltip(n) : String(n)
          }}
          labelFormatter={(d) => fmtShortDate(String(d))}
        />
        {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {series.map((s) => (
          <Bar key={s.id} dataKey={s.id} name={s.label} stackId="a" fill={s.color} isAnimationActive={false} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}

function fmtShortDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}
